import { type MaybeRefOrGetter, type Ref, computed, toValue } from 'vue';

type RovingItem = { disabled?: boolean };

type UseRovingTabsOptions<T extends RovingItem> = {
  tablistRef: Readonly<Ref<HTMLElement | null>>;
  items: MaybeRefOrGetter<readonly T[]>;
  selectedIndex: MaybeRefOrGetter<number>;
  onActivate: (item: T, index: number) => void;
  disabled?: MaybeRefOrGetter<boolean>;
  manualActivation?: MaybeRefOrGetter<boolean>;
};

const NAVIGATION_KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];

function nextEnabledPosition(key: string, current: number, count: number) {
  switch (key) {
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    case 'ArrowRight':
      return current < 0 ? 0 : (current + 1) % count;
    default:
      return current < 0 ? count - 1 : (current - 1 + count) % count;
  }
}

export function useRovingTabs<T extends RovingItem>({
  tablistRef,
  items,
  selectedIndex,
  onActivate,
  disabled,
  manualActivation,
}: UseRovingTabsOptions<T>) {
  const rovingIndex = computed(() => {
    if (toValue(disabled)) return -1;
    const list = toValue(items);
    const selected = toValue(selectedIndex);
    if (list[selected] && !list[selected].disabled) return selected;
    return list.findIndex((item) => !item.disabled);
  });

  function onKeydown(event: KeyboardEvent) {
    if (!NAVIGATION_KEYS.includes(event.key) || toValue(disabled)) return;

    const enabled = toValue(items)
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.disabled);
    if (!enabled.length) return;
    event.preventDefault();

    const buttons = Array.from(
      tablistRef.value?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [],
    );
    const activePosition = enabled.findIndex(
      ({ index }) => buttons[index] === document.activeElement,
    );
    const next =
      enabled[nextEnabledPosition(event.key, activePosition, enabled.length)];
    if (!next) return;
    buttons[next.index]?.focus();

    if (toValue(manualActivation)) return;
    onActivate(next.item, next.index);
  }

  function tabindexFor(index: number) {
    return index === rovingIndex.value ? 0 : -1;
  }

  return { rovingIndex, onKeydown, tabindexFor };
}
